import React from "react";
import { Link } from "react-router-dom";
import { Building2, Home as HomeIcon, Mail, MapPin, MessageCircle, Phone, Siren } from "lucide-react";
import { BRAND, NAV, SERVICE_INDEX } from "@/lib/site";
import { Stadtmusikanten, BremenSkyline } from "@/components/graphics/Marks";

const WA_PRIVAT = `${BRAND.whatsapp}?text=${encodeURIComponent(
  "Notruf 24/7 – Privat: Ich brauche kurzfristig Winterdienst in Bremen.",
)}`;
const WA_GEWERBE = `${BRAND.whatsapp}?text=${encodeURIComponent(
  "Notruf 24/7 – Gewerbe: Wir brauchen kurzfristig Winterdienst für unser Objekt in Bremen.",
)}`;

const EmergencyCard = ({ icon: Icon, group, text, wa, testid }) => (
  <div className="rounded-2xl bg-white/12 p-5 ring-1 ring-white/15 backdrop-blur-sm">
    <div className="flex items-center gap-2.5">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/15">
        <Icon className="h-[18px] w-[18px] text-white" />
      </span>
      <div>
        <p className="font-mono-label text-[9.5px] text-white/70">Notruf 24/7</p>
        <p className="text-[15px] font-bold leading-tight text-white">{group}</p>
      </div>
    </div>
    <p className="mt-3.5 text-[12px] leading-relaxed text-white/75">{text}</p>
    <a
      href={BRAND.phoneHref}
      data-testid={`footer-emergency-${testid}`}
      className="btn mt-4 h-11 w-full bg-white px-4 text-[13px] font-bold text-[#D81E2F] hover:bg-[#FFF1F2]"
    >
      <Siren className="h-4 w-4" /> Notruf 24/7 · {group}
    </a>
    <a
      href={wa}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`footer-emergency-wa-${testid}`}
      className="mt-2.5 flex items-center justify-center gap-2 rounded-full border border-white/25 py-2.5 text-[12px] font-semibold text-white transition-colors hover:border-white hover:bg-white/10"
    >
      <MessageCircle className="h-3.5 w-3.5" /> Per WhatsApp melden
    </a>
  </div>
);

const Footer = () => (
  <footer data-testid="site-footer" className="relative overflow-hidden bg-[#062B52] text-white">
    {/* ---------- Notruf 24/7 band ---------- */}
    <section data-testid="footer-emergency-band" className="relative overflow-hidden bg-[#D81E2F]">
      <Stadtmusikanten
        className="pointer-events-none absolute -left-8 bottom-0 h-[260px] opacity-[0.1]"
        color="#FFFFFF"
      />
      <div className="shell relative z-10 grid gap-9 py-12 lg:grid-cols-[1fr_1.25fr] lg:items-center lg:gap-16 lg:py-14">
        <div>
          <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1.5">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white/70" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-white" />
            </span>
            <span className="font-mono-label text-[9.5px] text-white">Notruf 24/7 im Einsatz</span>
          </span>
          <h2
            className="mt-5 text-[26px] font-bold leading-[1.15] sm:text-[32px] lg:text-[38px]"
            style={{ color: "#fff" }}
          >
            Schnee oder Glätte akut? Jetzt direkt melden.
          </h2>
          <p className="mt-4 max-w-md text-[14px] leading-relaxed text-white/80">
            Bereitschaft für betreute Objekte in Bremen – Einsätze erfolgen nach vereinbarten
            Prioritäten. Wählen Sie Ihren Bereich:
          </p>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <EmergencyCard
            icon={HomeIcon}
            group="Privat"
            text="Gehweg, Hauseingang, Treppe oder Einfahrt sind nicht mehr nutzbar."
            wa={WA_PRIVAT}
            testid="privat"
          />
          <EmergencyCard
            icon={Building2}
            group="Gewerbe"
            text="Parkplatz, Zufahrt, Rampe oder Betriebsgelände müssen frei werden."
            wa={WA_GEWERBE}
            testid="gewerbe"
          />
        </div>
      </div>
    </section>

    <Stadtmusikanten
      className="pointer-events-none absolute -left-10 bottom-0 h-[420px] opacity-[0.06]"
      color="#FFFFFF"
    />
    <BremenSkyline
      className="pointer-events-none absolute inset-x-0 bottom-0 h-28 w-full opacity-[0.08]"
      color="#FFFFFF"
    />

    <div className="shell relative z-10 pb-14 pt-20">
      <div className="grid gap-14 lg:grid-cols-[1.15fr_1fr_1fr_1.1fr]">
        <div>
          <div className="inline-flex rounded-2xl bg-white px-4 py-3">
            <img src={BRAND.logo} alt="Bremer Schneemann Logo" loading="lazy" decoding="async" className="h-14 w-auto" style={{ aspectRatio: "3 / 2" }} />
          </div>
          <p className="mt-6 text-2xl font-bold tracking-tight" style={{ fontFamily: "Archivo, sans-serif" }}>
            {BRAND.name}
          </p>
          <p className="mt-1 text-sm text-white/60">{BRAND.tagline}</p>
          <p className="mt-6 max-w-sm text-sm leading-relaxed text-white/60">
            Schneeräumung, Streudienst und Eisbeseitigung für Wege, Zufahrten, Parkplätze
            und Immobilien – persönlich betreut von {BRAND.owner}.
          </p>
        </div>

        <div>
          <p className="font-mono-label text-[#FF6B00]">Navigation</p>
          <ul className="mt-5 space-y-3 text-sm">
            {NAV.map((n) => (
              <li key={n.to}>
                <Link
                  to={n.to}
                  data-testid={`footer-nav-${n.to === "/" ? "home" : n.to.replace(/\//g, "")}`}
                  className="text-white/70 transition-colors hover:text-[#FF6B00]"
                >
                  {n.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <p className="font-mono-label text-[#FF6B00]">Leistungen</p>
          <ul className="mt-5 space-y-3 text-sm">
            {SERVICE_INDEX.slice(0, 9).map((s) => (
              <li key={s.to}>
                <Link
                  to={s.to}
                  data-testid={`footer-service-${s.to.replace(/\//g, "")}`}
                  className="text-white/70 transition-colors hover:text-[#FF6B00]"
                >
                  {s.title}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <p className="font-mono-label text-[#FF6B00]">Kontakt</p>
          <div className="mt-5 space-y-4 text-sm">
            <a href={BRAND.phoneHref} data-testid="footer-phone" className="flex items-start gap-3 text-white/80 hover:text-white">
              <Phone className="mt-0.5 h-4 w-4 text-[#FF6B00]" /> {BRAND.phone}
            </a>
            <a
              href={BRAND.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-whatsapp"
              className="flex items-start gap-3 text-white/80 hover:text-white"
            >
              <MessageCircle className="mt-0.5 h-4 w-4 text-[#25D366]" /> WhatsApp
            </a>
            <a href={BRAND.emailHref} data-testid="footer-email" className="flex items-start gap-3 break-all text-white/80 hover:text-white">
              <Mail className="mt-0.5 h-4 w-4 text-[#FF6B00]" /> {BRAND.email}
            </a>
            <p className="flex items-start gap-3 text-white/70">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[#FF6B00]" />
              <span>
                <strong className="font-semibold text-white/90">Hauptbüro</strong>
                <br />
                {BRAND.street}
                <br />
                {BRAND.streetExtra}
                <br />
                {BRAND.zip} {BRAND.city}
                <br />
                <span className="mt-1.5 block text-white/55">{BRAND.fleet}</span>
              </span>
            </p>
          </div>
          <div className="mt-7 grid gap-2.5">
            <Link to="/kontakt" data-testid="footer-cta" className="btn btn-orange h-12 px-5 text-sm">
              Winterdienst anfragen
            </Link>
          </div>
        </div>
      </div>

      <div className="mt-16 flex flex-col gap-5 border-t border-white/12 pt-7 text-xs text-white/50 md:flex-row md:items-center md:justify-between">
        <p>
          © {new Date().getFullYear()} {BRAND.name} · Inhaber {BRAND.owner} · USt-IdNr. {BRAND.vatId}
        </p>
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 lg:pr-[250px]">
          <Link to="/impressum" data-testid="footer-impressum" className="hover:text-white">
            Impressum
          </Link>
          <Link to="/datenschutz" data-testid="footer-datenschutz" className="hover:text-white">
            Datenschutz
          </Link>
          <Link to="/cookie-einstellungen" data-testid="footer-cookies" className="hover:text-white">
            Cookie-Einstellungen
          </Link>
        </div>
      </div>
    </div>
  </footer>
);

export default Footer;
